import { create } from 'zustand'
import { DEFAULT_LANGUAGE, DEFAULT_TIMEZONE } from '@/domains/shared/constants'
import type { User } from '@/types/User'

// accessToken - in memory only, never persisted. The refresh token is an httpOnly
// cookie the API sets (inovio.session); this code never sees it. See
// apps/web/CLAUDE.md, "Auth: sessions across tabs".

const LANGUAGE_KEY = 'inovio.language'
const TIMEZONE_KEY = 'inovio.timezone'

function saveLanguage(language: string) {
  localStorage.setItem(LANGUAGE_KEY, language)
}

function loadLanguage(): string {
  return localStorage.getItem(LANGUAGE_KEY) ?? DEFAULT_LANGUAGE
}

function saveTimezone(timezone: string) {
  localStorage.setItem(TIMEZONE_KEY, timezone)
}

function loadTimezone(): string {
  return localStorage.getItem(TIMEZONE_KEY) ?? DEFAULT_TIMEZONE
}

interface AuthState {
  user: User | null
  accessToken: string | null
  hydrating: boolean
  language: string
  timezone: string
}

interface AuthActions {
  setAuth: (accessToken: string, user: User) => void
  setHydrating: (value: boolean) => void
  setLanguage: (language: string) => void
  setTimezone: (timezone: string) => void
  logout: () => void
  isAuthenticated: () => boolean
  getBaseURL: () => string
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()((set, get) => ({
  user: null,
  accessToken: null,
  hydrating: true,
  language: loadLanguage(),
  timezone: loadTimezone(),

  setAuth: (accessToken: string, user: User) => {
    set({ accessToken, user })
  },

  setHydrating: (value: boolean) => {
    set({ hydrating: value })
  },

  setLanguage: (language: string) => {
    saveLanguage(language)
    set({ language })
  },

  setTimezone: (timezone: string) => {
    saveTimezone(timezone)
    set({ timezone })
  },

  logout: () => {
    set({ accessToken: null, user: null })
  },

  isAuthenticated: () => {
    const { accessToken } = get()
    if (!accessToken) return false
    try {
      const parts = accessToken.split('.')
      if (parts.length !== 3) return false
      const payload = JSON.parse(atob(parts[1]))
      return typeof payload.exp === 'number' && Date.now() / 1000 < payload.exp
    } catch {
      return false
    }
  },

  getBaseURL: () => `${import.meta.env.VITE_API_URL}`,
}))
